import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios, { AxiosError } from 'axios';

import {
    Box,
    VStack,
    HStack,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Textarea,
    useBoolean,
} from '@chakra-ui/react';

import PageTitle from '../../components/PageTitle';
import ToastBox from '../../components/ToastBox';
import { ToastBoxContext } from '../../contexts/ToastBoxContext';
import apis from '../../utils/apis';
import { Scoping } from '../Scoping';
import SelectField from '../../components/SelectField';
import Loading from '../../components/Loading';

export interface SynthesisFormData {
    scopingId?: string;
    threatnActions: {
        threat: string;
        action: string;
    }[];
    opportunitynActions: {
        opportunity: string;
        action: string;
    }[];
}

function Synthesis() {
    const {
        toast,
        showToast,
        onCloseToast,
    } = useContext(ToastBoxContext);

    const [threats, setThreats] = useState<string>('');
    const [threatsError, setThreatsError] = useState('');
    const [threatsAction, setThreatsAction] = useState<string>('');
    const [threatsActionError, setThreatsActionError] = useState('');

    const handleThreatsChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        if (threatsError) {
            setThreatsError('');
            return;
        }
        setThreats(e.target.value);
    }, [threatsError]);

    const handleThreatsActionChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
        if (threatsActionError) {
            setThreatsActionError('');
            return;
        }
        setThreatsAction(e.target.value);
    }, [threatsActionError]);

    const [opportunities, setOpportunities] = useState<string>('');
    const [opportunitiesError, setOpportunitiesError] = useState('');
    const [opportunitiesAction, setOpportunitiesAction] = useState<string>('');
    const [opportunitiesActionError, setOpportunitiesActionError] = useState('');

    const handleOpportunitiesChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            if (opportunitiesError) {
                setOpportunitiesError('');
                return;
            }
            setOpportunities(e.target.value);
        },
        [opportunitiesError],
    );

    const handleOpportunitiesActionChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            if (opportunitiesActionError) {
                setOpportunitiesActionError('');
                return;
            }
            setOpportunitiesAction(e.target.value);
        },
        [opportunitiesActionError],
    );

    useEffect(
        () => {
            if (toast) {
                onCloseToast();
            }
        },
        [onCloseToast, toast],
    );

    const onSaveSynthesis = useCallback(
        async (formData: SynthesisFormData) => {
            try {
                const requestBody = formData;
                const apiUrl = 'susaf/scoping/add';
                const response = await apis.post(apiUrl, requestBody);
                if (response.status === 200) {
                    const responseData: SynthesisFormData = response.data;

                    const successToastComponent = responseData && toast && toast({
                        status: 'success',
                        isClosable: true,
                        variant: 'subtle',
                        id: undefined,
                        duration: null,
                        position: 'top',
                        render: () => (
                            <ToastBox
                                onCloseToast={onCloseToast}
                                title="Product scoping added"
                                description="Product scoping has been added successfully"
                                status="success"
                            />
                        ),
                    });
                    showToast(successToastComponent);
                }
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    return;
                }
                const axiosError = error as AxiosError;
                const dataError = axiosError?.response?.data;
                console.warn({ dataError });
                const failureToastComponent = toast && toast({
                    status: 'error',
                    isClosable: true,
                    variant: 'subtle',
                    id: undefined,
                    duration: null,
                    position: 'top',
                    render: () => (
                        <ToastBox
                            onCloseToast={onCloseToast}
                            title="Error adding"
                            description="Could not add. Try again"
                            status="error"
                        />
                    ),
                });

                showToast(failureToastComponent);
            }
        },
        [onCloseToast, showToast, toast],
    );

    const onClearForm = useCallback(
        () => {
            setThreats('');
            setThreatsAction('');
            setOpportunities('');
            setOpportunitiesAction('');
        },
        [],
    );

    const [loadingScoping, setLoadingScoping] = useBoolean();
    const [scopingList, setScopingList] = useState<Scoping[]>();

    const getSavedScopings = useCallback(
        async () => {
            try {
                setLoadingScoping.on();
                const apiUrl = '/susaf/scoping/get/all';
                const response = await apis.get(apiUrl);
                const dataResponse: Scoping[] = await response.data;
                if (dataResponse.length <= 0) {
                    setLoadingScoping.off();
                    return;
                }
                setScopingList(dataResponse);
                setLoadingScoping.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR

                console.warn({ error });
            }
        },
        [setLoadingScoping],
    );

    useEffect(() => {
        getSavedScopings();
    }, [getSavedScopings]);

    const scopingOptions = useMemo(() => {
        if (!scopingList) {
            return [];
        }
        const mappedScopingList = scopingList.map((scoping) => ({
            label: scoping.productName,
            value: scoping.id ?? '',
        }));

        const defaultOption = {
            label: 'Select a Scope name',
            value: '',
        };

        mappedScopingList.splice(0, 0, defaultOption);

        return mappedScopingList;
    }, [scopingList]);

    const [selectedScopingId, setSelectedScopingId] = useState<string>();
    const onSelectScoping = useCallback(
        (value: string) => setSelectedScopingId(value),
        [],
    );

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            if (!threats) {
                setThreatsError('Please enter threats');
                return;
            }
            if (!threatsAction) {
                setThreatsActionError('Please enter actions for threats');
                return;
            }

            if (!opportunities) {
                setOpportunitiesError('Please enter opportunities');
                return;
            }
            if (!opportunitiesAction) {
                setOpportunitiesActionError('Please enter actions for opportunities');
                return;
            }

            if (!selectedScopingId) {
                return;
            }

            onSaveSynthesis({
                scopingId: selectedScopingId,
                threatnActions: [
                    {
                        threat: threats,
                        action: threatsAction,

                    },
                ],
                opportunitynActions: [
                    {
                        opportunity: opportunities,
                        action: opportunitiesAction,

                    },
                ],
            });
            onClearForm();
        },
        [
            onClearForm,
            onSaveSynthesis,
            opportunities,
            opportunitiesAction,
            selectedScopingId,
            threats,
            threatsAction,
        ],
    );

    return (
        <VStack>
            <PageTitle
                title="Synthesis: Threats, opportunities, actions"
            />
            {loadingScoping && (
                <Box width="100%">
                    <Loading message="Loading scopings" />
                </Box>
            )}
            <Box
                width="100%"
                alignItems="flex-start"
                background="#fff"
            >
                {scopingList && scopingList.length > 0 && (
                    <form onSubmit={handleSubmit}>
                        <VStack
                            spacing={8}
                            borderWidth={1}
                            padding={4}
                            width="100%"
                        >
                            <SelectField
                                options={scopingOptions}
                                label="Select a Scope"
                                onSelectOption={onSelectScoping}
                                value={selectedScopingId}
                            />
                            <HStack justifyContent="space-between" width="100%" spacing={16}>
                                <FormControl isInvalid={!!threatsError}>
                                    <FormLabel htmlFor="threats">
                                        <b>
                                            Threats
                                        </b>
                                    </FormLabel>
                                    <Textarea
                                        id="threats"
                                        value={threats}
                                        onChange={handleThreatsChange}
                                        placeholder="Enter threats"
                                        rows={6}
                                        autoComplete="off"
                                    />
                                    {threatsError && (
                                        <FormErrorMessage>
                                            {threatsError}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                                <FormControl isInvalid={!!threatsActionError}>
                                    <FormLabel htmlFor="threatsAction">
                                        <b>
                                            Action
                                        </b>
                                    </FormLabel>
                                    <Textarea
                                        id="threatsAction"
                                        value={threatsAction}
                                        onChange={handleThreatsActionChange}
                                        placeholder="Enter actions for threats"
                                        rows={6}
                                        autoComplete="off"
                                    />
                                    {threatsActionError && (
                                        <FormErrorMessage>
                                            {threatsActionError}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </HStack>
                            <HStack justifyContent="space-between" width="100%" spacing={16}>
                                <FormControl isInvalid={!!opportunitiesError}>
                                    <FormLabel htmlFor="opportunities">
                                        <b>
                                            Opportunities
                                        </b>
                                    </FormLabel>
                                    <Textarea
                                        id="opportunities"
                                        value={opportunities}
                                        onChange={handleOpportunitiesChange}
                                        placeholder="Enter opportunities"
                                        rows={6}
                                        autoComplete="off"
                                    />
                                    {opportunitiesError && (
                                        <FormErrorMessage>
                                            {opportunitiesError}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                                <FormControl isInvalid={!!opportunitiesActionError}>
                                    <FormLabel htmlFor="opportunitiesAction">
                                        <b>
                                            Action
                                        </b>
                                    </FormLabel>
                                    <Textarea
                                        id="opportunitiesAction"
                                        value={opportunitiesAction}
                                        onChange={handleOpportunitiesActionChange}
                                        placeholder="Enter actions for opportunities"
                                        rows={6}
                                        autoComplete="off"
                                    />
                                    {opportunitiesActionError && (
                                        <FormErrorMessage>
                                            {opportunitiesActionError}
                                        </FormErrorMessage>
                                    )}
                                </FormControl>
                            </HStack>
                            <HStack
                                width="100%"
                                justifyContent="flex-end"
                                spacing={4}
                            >
                                <Button
                                    type="reset"
                                    colorScheme="brand"
                                    variant="outline"
                                    letterSpacing={1}
                                    onClick={onClearForm}
                                    py={4}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    letterSpacing={1}
                                    colorScheme="brand"
                                    py={4}
                                >
                                    Submit
                                </Button>
                            </HStack>
                        </VStack>
                    </form>
                )}
            </Box>
        </VStack>
    );
}

export default Synthesis;
